import { Checkbox, TextArea, Typography } from '@neo4j-ndl/react';
import { ExclamationCircleIconSolid } from '@neo4j-ndl/react/icons';
import { CHURN_REASON } from '@nx/state';
import { Objects, type Validation } from '@nx/stdlib';
import { useMemo } from 'react';

import { type ChurnSurveyData, shuffle } from './helpers';

const churnReasonLabels: { [key in CHURN_REASON]: string } = {
  [CHURN_REASON.EXPENSIVE]: 'Too expensive',
  [CHURN_REASON.DIFFICULT]: 'Difficult to use',
  [CHURN_REASON.FEATURES]: 'Missing key features',
  [CHURN_REASON.PERFORMANCE]: 'Poor performance',
  [CHURN_REASON.FOUND_ALTERNATIVE]: 'Found a better alternative',
  [CHURN_REASON.DOCUMENTATION]: 'Lack of proper documentation',
  [CHURN_REASON.OTHER]: 'Other (please specify)',
};

interface ChurnSurveyProps {
  data: ChurnSurveyData;
  onChange: (data: ChurnSurveyData) => void;
  validation: Validation<ChurnSurveyData> | null;
}

export const ChurnSurvey = ({ data, onChange, validation }: ChurnSurveyProps) => {
  const churnReasonsLabelsRandomized = useMemo(() => {
    const entriesWithoutOther = Objects.entries(churnReasonLabels).filter(([k, _]) => k !== CHURN_REASON.OTHER);
    const shuffledEntries = shuffle(entriesWithoutOther);
    return shuffledEntries.concat([[CHURN_REASON.OTHER, churnReasonLabels[CHURN_REASON.OTHER]]]);
  }, []);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Typography variant="subheading-medium">Reason for deletion</Typography>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            {churnReasonsLabelsRandomized.map(([value, label]) => (
              <Checkbox
                key={value}
                label={label}
                onChange={({ target: { checked } }) =>
                  onChange({ ...data, reasons: { ...data.reasons, [value]: checked } })
                }
                value={value}
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                isChecked={data.reasons[value as CHURN_REASON]}
              />
            ))}
          </div>
          {validation?.reasons?.error && (
            <div className="text-danger-text flex gap-1">
              <ExclamationCircleIconSolid className=" h-5" />
              <Typography variant="body-small">{validation.reasons.message}</Typography>
            </div>
          )}
        </div>
      </div>
      <TextArea
        label={
          <Typography variant="subheading-medium" className="text-neutral-text-default">
            Any additional comments for our product team?
          </Typography>
        }
        placeholder="Let us know..."
        value={data.comment}
        errorText={validation?.comment?.message}
        htmlAttributes={{
          onChange: ({ target: { value: comment } }) => onChange({ ...data, comment }),
          maxLength: 1500,
          className: 'resize-none',
        }}
        isFluid
      />
    </div>
  );
};
